import React, { useMemo } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { color, space, flexbox, layout } from 'styled-system';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Card from 'theme/card';
import Sidebar from 'theme/sidebar';
import Header from 'theme/header';
import AuthSwitch from 'components/auth-switch';
import CounterCards from 'components/counter-cards';
import NewsCards from 'components/news-cards';
import WarningNotification from 'components/warning-notification';
import AssetsRevenues from 'components/assets-revenues-dash';
import AreaCharts from 'components/area-chart';
import TopBar from 'components/top-bar';
import ScopeGate from 'components/scope-gate';
import LockedSection from 'theme/locked-section';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  /* Below the first breakpoint the two columns stop being columns: on a phone
     the right-hand one (add content, services, history, top selling) squeezed
     the charts into a strip. Stacked, nothing is lost and nothing is cramped. */
  .row {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  @media (min-width: ${themeGet('breakpoints.0')}) {
    .row {
      flex-direction: row;
    }
  }
`;

const Content = styled.div`
  ${space}

  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const Section = styled.div`
  ${space}
  ${color}
  ${layout}

  display: flex;
  /* A phone cannot hold three cards side by side: each of them was a third of
     the screen, so the figures spilled over their own card and the page
     scrolled sideways. Stacked below the first breakpoint, side by side above. */
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${themeGet('breakpoints.0')}) {
    flex-direction: row;
  }
`;

const WrapStats = styled.div`
  ${flexbox}

  display: flex;

  & > div:first-child {
    flex: 1;
  }
`;

function DashboardPage() {
  const { formatDate } = useSelector((state) => state.config);

  const dateTo = useMemo(() => {
    const today = moment();

    return today.format(formatDate);
  }, [formatDate]);

  const dateFrom = useMemo(() => {
    const today = moment();

    return today.subtract(30, 'days').format(formatDate);
  }, [formatDate]);

  return (
    <AuthSwitch>
      <Wrapper>
        <TopBar />

        <Header bg="white" title="StreetLib Dashboard" />

        <div className="row">
          <Content pb="m" px="s">
            <Section maxWidth="1440px" width="100%">
              <WarningNotification
                style={{
                  p: 's',
                  mb: 'm',
                }}
              />
            </Section>

            <Section mb="m" width="100%" maxWidth="1440px">
              <CounterCards />
            </Section>

            <Section mb="m" width="100%">
              <Card width="100%" bg="primary_50" border="none" py="m" px="s">
                <ScopeGate
                  section="analytics"
                  fallback={<LockedSection width="100%" minHeight="280px" />}
                >
                  <WrapStats flexDirection={['column', 'column', 'column', 'column', 'row']}>
                    <Card
                      width={['100%', '100%', '100%', '100%', '50%']}
                      bg="white"
                      border="none"
                      py="m"
                    >
                      <AreaCharts
                        interval="day"
                        dateTo={dateTo}
                        dateFrom={dateFrom}
                        dataType="temporary_data"
                      />
                    </Card>
                    <Card
                      width={['100%', '100%', '100%', '100%', '50%']}
                      bg="white"
                      border="none"
                      py="m"
                      px="s"
                      mt={['s', 's', 's', 's', '0']}
                      ml={['0', '0', '0', '0', 's']}
                    >
                      <AssetsRevenues
                        dateTo={dateTo}
                        dateFrom={dateFrom}
                        dataType="temporary_data"
                      />
                    </Card>
                  </WrapStats>
                </ScopeGate>
              </Card>
            </Section>
            <Section mb="m" width="100%" maxWidth="1440px">
              <NewsCards />
            </Section>
          </Content>
          <Sidebar />
        </div>
      </Wrapper>
    </AuthSwitch>
  );
}

export default DashboardPage;
